import React from 'react';
import { FileText, CheckCircle, XCircle, Scale, AlertTriangle, Shield, RefreshCw, Mail } from 'lucide-react';
import SEO, { PAGE_SEO } from './SEO';
import { Link } from 'react-router-dom';

const Terms: React.FC = () => {
  const lastUpdated = "August 25, 2026";

  const sections = [
    {
      icon: CheckCircle,
      title: "Acceptance of Terms",
      content: `By accessing or using ClubIQ, you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access the service.

These terms apply to all visitors, users, and others who access or use ClubIQ.`
    },
    {
      icon: FileText,
      title: "Description of Service",
      content: `ClubIQ is a golf club management and optimization platform that helps golfers:

• Track and organize their golf equipment
• Calculate and analyze club distances
• Receive personalized recommendations for bag optimization
• Access tools for shot selection and course strategy

ClubIQ is provided "as is" and is intended for informational and entertainment purposes. While we strive for accuracy, golf performance depends on many factors beyond club selection.`
    },
    {
      icon: Shield,
      title: "User Accounts",
      content: `When you create an account with ClubIQ, you must provide accurate and complete information. You are responsible for:

• Maintaining the security of your account and password
• All activities that occur under your account
• Notifying us immediately of any unauthorized access

You must be at least 13 years old to use ClubIQ. If you are under 18, you must have parental consent to use the service.`
    },
    {
      icon: CheckCircle,
      title: "Acceptable Use",
      content: `You agree to use ClubIQ only for lawful purposes and in accordance with these Terms. You agree NOT to:

• Use the service in any way that violates applicable laws or regulations
• Attempt to gain unauthorized access to any part of the service
• Use the service to transmit malicious code or interfere with its operation
• Impersonate or attempt to impersonate ClubIQ, its employees, or other users
• Use any automated means to access the service without our permission
• Share your account credentials with others

We reserve the right to terminate or suspend your account for violations of these terms.`
    },
    {
      icon: XCircle,
      title: "Prohibited Activities",
      content: `The following activities are strictly prohibited:

• Scraping, data mining, or harvesting user information
• Reverse engineering or attempting to extract source code
• Using the service for commercial purposes without authorization
• Creating multiple accounts to abuse features or circumvent restrictions
• Posting false, misleading, or defamatory content
• Harassing, abusing, or threatening other users

Violation of these prohibitions may result in immediate termination of your account.`
    },
    {
      icon: Scale,
      title: "Intellectual Property",
      content: `ClubIQ and its original content, features, and functionality are owned by Asgard Technologies, LLC and are protected by international copyright, trademark, and other intellectual property laws.

**Your Content**: You retain ownership of any content you submit to ClubIQ (such as your golf profile data). By submitting content, you grant us a license to use, store, and process it to provide the service.

**Feedback**: Any feedback, suggestions, or ideas you provide about ClubIQ may be used by us without any obligation to you.`
    },
    {
      icon: Shield,
      title: "Artificial Intelligence",
      content: `ClubIQ uses AI, including OpenAI, to generate distances, equipment suggestions, club-photo identification, and chat answers. AI output can be wrong. Use your own judgment on the course.

ClubIQ is not a medical, mental-health, or emergency service. If you are in crisis in the United States, call or text 988.`
    },
    {
      icon: RefreshCw,
      title: "Billing, Cancellation, and Auto-Renewal",
      content: `There is no paid ClubIQ plan today. Checkout is closed until an owner-approved price exists. ClubIQ does not take card numbers and does not auto-renew.

When a paid plan exists:
• Sign-up and cancel will both be available in the account settings page
• Cancel will take one step, not a longer process than creating an account
• We will send a renewal reminder before each automatic charge
• Paid access ends at the close of the paid period after you cancel

Until that price exists, there is nothing to cancel except your free account, which you can delete in Profile.`
    },
    {
      icon: CheckCircle,
      title: "Reviews and Testimonials",
      content: `ClubIQ does not publish customer quotes, star ratings, or named results that we cannot verify. Homepage copy describes product features, not invented golfers.`
    },
    {
      icon: AlertTriangle,
      title: "Disclaimers",
      content: `**No Warranty**: ClubIQ is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.

**Golf Performance**: ClubIQ provides tools and recommendations based on the information you provide. We do not guarantee any specific improvement in your golf game. Actual results depend on many factors including skill, practice, conditions, and equipment.

**Accuracy**: While we strive for accuracy in our calculations and recommendations, we cannot guarantee that all information is error-free. Always use your judgment on the course.`
    },
    {
      icon: Shield,
      title: "Limitation of Liability",
      content: `To the maximum extent permitted by law, Asgard Technologies, LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:

• Loss of profits, data, use, or goodwill
• Personal injury or property damage
• Any damages arising from your use or inability to use the service
• Any unauthorized access to or alteration of your data

Our total liability for any claims arising from your use of ClubIQ shall not exceed the amount you paid us (if any) in the past twelve months.`
    },
    {
      icon: Scale,
      title: "Governing Law",
      content: `These Terms shall be governed by and construed in accordance with the laws of the State of Georgia, United States, without regard to its conflict of law provisions.

Any disputes arising from these Terms or your use of ClubIQ shall be resolved in the state or federal courts located in Georgia, and you consent to the personal jurisdiction of such courts.`
    },
    {
      icon: RefreshCw,
      title: "Changes to Terms",
      content: `We reserve the right to modify or replace these Terms at any time at our sole discretion. If we make material changes, we will provide notice through the service or by other means.

Your continued use of ClubIQ after any changes to the Terms constitutes acceptance of those changes. We encourage you to review these Terms periodically.`
    },
    {
      icon: XCircle,
      title: "Termination",
      content: `We may terminate or suspend your account and access to ClubIQ immediately, without prior notice or liability, for any reason, including but not limited to breach of these Terms.

Upon termination, your right to use the service will immediately cease. If you wish to terminate your account, use Delete my account in Profile. That path is shorter than creating an account.

Provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.`
    },
    {
      icon: Mail,
      title: "Contact Us",
      content: `If you have any questions about these Terms of Service, please contact us through our contact form.

**Asgard Technologies, LLC**
Georgia, USA`
    }
  ];

  return (
    <>
      <SEO {...PAGE_SEO.terms} url="/terms" />
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl mb-6">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            The rules of the road for using ClubIQ. Please read carefully.
          </p>
          <p className="text-white/60 mt-4 text-sm">
            Last updated: {lastUpdated}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Quick Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 md:p-8 mb-12">
          <h2 className="text-xl font-bold text-blue-800 mb-4">The Basics</h2>
          <ul className="space-y-3 text-blue-700">
            <li className="flex items-start gap-3">
              <span className="text-blue-500 mt-1">→</span>
              <span>By using ClubIQ, you agree to these terms</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-500 mt-1">→</span>
              <span>Use the service responsibly and legally</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-500 mt-1">→</span>
              <span>We provide tools and recommendations, not guarantees</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-500 mt-1">→</span>
              <span>Questions? <Link to="/contact" className="underline hover:text-blue-800">Contact us</Link></span>
            </li>
          </ul>
        </div>

        {/* Detailed Sections */}
        <div className="space-y-12">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <section key={index} className="scroll-mt-24" id={section.title.toLowerCase().replace(/\s+/g, '-')}>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-gray-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 pt-2">{section.title}</h2>
                </div>
                <div className="pl-16">
                  <div 
                    className="prose prose-gray max-w-none text-gray-600 leading-relaxed"
                    dangerouslySetInnerHTML={{ 
                      __html: section.content
                        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900">$1</strong>')
                        .replace(/\n\n/g, '</p><p class="mt-4">')
                        .replace(/\n• /g, '</p><p class="mt-2 pl-4">• ')
                    }}
                  />
                </div>
              </section>
            );
          })}
        </div>

        {/* Footer Note */}
        <div className="mt-16 pt-8 border-t border-gray-200">
          <p className="text-gray-500 text-sm text-center">
            These Terms of Service are effective as of {lastUpdated}. By continuing to use ClubIQ, 
            you acknowledge that you have read, understood, and agree to be bound by these terms.
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default Terms;
